/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import APIHelper from '../../api/APIHelper';
import envConfig from '../../config/envConfig';
import { useAlertDialog } from '../common/AlertDialogContext';
import ProgressBarDialog from '../common/ProgressBar';
import Paginator from '../common/Paginator';

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret
});

export default function EventsTab({ applicationId, navigate }) {
    const { theme } = useTheme();
    const { showAlertDialog } = useAlertDialog();

    const [loading, setLoading] = useState(true);
    const [events, setEvents] = useState([]);
    const [registry, setRegistry] = useState([]);
    const [tables, setTables] = useState([]);

    // Form State
    const [showForm, setShowForm] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);
    const [formData, setFormData] = useState({
        event_type: '',
        event_key: '',
        display_name: '',
        description: '',
        table_scope: 'all',
        scoped_tables: []
    });
    const [formErrors, setFormErrors] = useState({});

    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 6;

    const fetchStaticData = useCallback(async () => {
        try {
            const [regResp, tbResp] = await Promise.all([
                apiHelper.apiCall('/arcee/events/registry/', { method: 'GET' }),
                apiHelper.apiCall(`/arcee/application/table/list/?application_id=${applicationId}&page=1&page_size=100`, { method: 'GET' })
            ]);

            if (regResp.ok) {
                const data = await regResp.json();
                let mappedRegistry = [];
                const payload = data.results || data.data || data;
                if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
                    mappedRegistry = Object.keys(payload).map(k => {
                        const arr = Array.isArray(payload[k]) ? payload[k] : [];
                        return {
                            type: k,
                            display_name: k.charAt(0).toUpperCase() + k.slice(1) + ' Events',
                            properties: {
                                event_keys: arr.map(item => ({
                                    key: item.key || item,
                                    display_name: item.display_name || item,
                                    description: item.description || ''
                                }))
                            }
                        };
                    });
                }
                setRegistry(mappedRegistry);
            }
            if (tbResp.ok) {
                const data = await tbResp.json();
                const tbList = Array.isArray(data) ? data : (data.results || []);
                setTables(tbList);
            }
        } catch (error) {
            console.error("Error fetching static data", error);
        }
    }, [applicationId]);

    const fetchEvents = useCallback(async () => {
        setLoading(true);
        try {
            const evtResp = await apiHelper.apiCall(`/arcee/events/?page=${currentPage}&page_size=${itemsPerPage}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ application_id: parseInt(applicationId, 10) })
            });

            if (evtResp.ok) {
                const data = await evtResp.json();
                setEvents(data.results || []);
                setTotalRecords(data.count || 0);
            }
        } catch (error) {
            console.error("Error fetching events", error);
            showAlertDialog({ title: 'Error', message: 'Failed to load events.', type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [applicationId, currentPage, itemsPerPage, showAlertDialog]);

    useEffect(() => {
        fetchStaticData();
    }, [fetchStaticData]);

    useEffect(() => {
        fetchEvents();
    }, [fetchEvents]);

    const handleFormOpen = (evt = null) => {
        if (evt) {
            setEditingEvent(evt);
            setFormData({
                event_type: evt.event_type || '',
                event_key: evt.event_key || '',
                display_name: evt.display_name || '',
                description: evt.description || '',
                table_scope: evt.table_scope || 'all',
                scoped_tables: evt.scoped_tables || []
            });
        } else {
            setEditingEvent(null);
            setFormData({
                event_type: '',
                event_key: '',
                display_name: '',
                description: '',
                table_scope: 'all',
                scoped_tables: []
            });
        }
        setFormErrors({});
        setShowForm(true);
    };

    const handleFormClose = () => {
        setShowForm(false);
        setEditingEvent(null);
    };

    // Auto-fill display_name when event_key is selected, if left empty mapping from registry
    const handleEventKeyChange = (val) => {
        let defaultName = '';
        if (formData.event_type) {
            const regType = registry.find(r => r.type === formData.event_type);
            if (regType) {
                const regKey = regType.properties?.event_keys?.find(k => k.key === val);
                if (regKey) defaultName = regKey.display_name;
            }
        }
        setFormData(prev => {
            // Find the previously selected event's display_name so we know if
            // the user manually typed something or it was auto-filled last time
            let prevDefaultName = '';
            if (formData.event_type) {
                const regType = registry.find(r => r.type === formData.event_type);
                if (regType) {
                    const prevKey = regType.properties?.event_keys?.find(k => k.key === prev.event_key);
                    if (prevKey) prevDefaultName = prevKey.display_name;
                }
            }
            // Update display_name if it's empty OR still matches the previous auto-filled value
            const shouldAutoFill = !prev.display_name.trim() || prev.display_name === prevDefaultName;
            return {
                ...prev,
                event_key: val,
                display_name: shouldAutoFill ? defaultName : prev.display_name,
            };
        });
    };

    const handleToggleScopedTable = (tableId) => {
        setFormData(prev => {
            const currentTables = prev.scoped_tables || [];
            const newTables = currentTables.includes(tableId)
                ? currentTables.filter(id => id !== tableId)
                : [...currentTables, tableId];
            return { ...prev, scoped_tables: newTables };
        });
    };

    const validateForm = () => {
        const errs = {};
        if (!formData.event_type) errs.event_type = 'Event Category is required.';
        if (!formData.event_key) errs.event_key = 'Specific Event is required.';
        if (!formData.display_name.trim()) errs.display_name = 'Display Name is required.';
        if (formData.table_scope !== 'all' && (!formData.scoped_tables || formData.scoped_tables.length === 0)) {
            errs.scoped_tables = 'Select at least one table when scope is restricted.';
        }
        setFormErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        const payload = {
            application_id: parseInt(applicationId, 10),
            event_type: formData.event_type,
            event_key: formData.event_key,
            display_name: formData.display_name,
            description: formData.description,
            table_scope: formData.table_scope,
            scoped_tables: formData.table_scope === 'all' ? [] : formData.scoped_tables
        };

        try {
            let resp;
            if (editingEvent) {
                resp = await apiHelper.apiCall(`/arcee/events/${editingEvent.id}/`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
            } else {
                resp = await apiHelper.apiCall('/arcee/events/create/', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
            }

            if (!resp.ok) {
                const err = await resp.json().catch(() => ({}));
                const errStr = typeof err === 'object' && !err.error
                    ? Object.values(err).map(v => Array.isArray(v) ? v.join(', ') : String(v)).join(' ')
                    : (err.error || 'Failed to save event');
                throw new Error(errStr);
            }

            handleFormClose();
            fetchEvents();
        } catch (error) {
            showAlertDialog({ title: 'Error', message: error.message, type: 'error' });
        }
    };

    const handleDelete = (evt) => {
        showAlertDialog({
            title: 'Delete Event Config',
            message: `Are you sure you want to delete "${evt.display_name}"? This will also remove any triggers attached to it.`,
            type: 'warning',
            showCancel: true,
            onOk: async () => {
                try {
                    const resp = await apiHelper.apiCall(`/arcee/events/${evt.id}/`, { method: 'DELETE' });
                    if (!resp.ok) throw new Error('Failed to delete');
                    fetchEvents();
                } catch (error) {
                    showAlertDialog({ title: 'Error', message: error.message, type: 'error' });
                }
            }
        });
    };

    const activeRegistryType = registry.find(r => r.type === formData.event_type);

    const pageCount = Math.max(1, Math.ceil(totalRecords / itemsPerPage));
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pageCount) setCurrentPage(newPage);
    };
    const handlePrevious = () => setCurrentPage(p => Math.max(p - 1, 1));
    const handleNext = () => setCurrentPage(p => Math.min(p + 1, pageCount));

    return (
        <div className="w-100">
            {loading ? <ProgressBarDialog open={true} text_message="Loading events..." /> : null}

            <div className="d-flex align-items-center justify-content-between mb-4 mt-2">
                <div>
                    <h5 className="fw-bold mb-1">Configured Events</h5>
                    <p className="text-muted small mb-0">Events determine what logic/actions are tracked in this application.</p>
                </div>
                <button
                    className="btn btn-primary d-flex align-items-center gap-2 shadow-sm"
                    onClick={() => handleFormOpen()}
                    style={{ background: theme.primary, border: 'none', borderRadius: theme.buttonRadius || '6px' }}
                >
                    <i className="bi bi-plus-lg"></i>
                    Add Event
                </button>
            </div>

            {/* List Events */}
            {events.length === 0 && !loading && (
                <div className="text-center text-muted py-5 bg-white rounded shadow-sm border border-dashed">
                    <i className="bi bi-inbox fs-1 mb-3 d-block opacity-50"></i>
                    No events configured yet.<br />
                    <small>Click "Add Event" to start monitoring application events.</small>
                </div>
            )}

            <div className="row g-3">
                {events.map(evt => (
                    <div className="col-12 col-md-6 col-lg-4" key={evt.id}>
                        <div className="card h-100 border-0 shadow-sm" style={{ borderRadius: theme.cardRadius || '8px', minHeight: '220px' }}>
                            <div className="card-body p-4 position-relative d-flex flex-column">
                                <div className="position-absolute top-0 end-0 p-3 pt-3">
                                    <div className="dropdown">
                                        <button className="btn btn-sm btn-light border-0" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                                            <i className="bi bi-three-dots-vertical"></i>
                                        </button>
                                        <ul className="dropdown-menu dropdown-menu-end shadow border-0" style={{ borderRadius: '8px', fontSize: '14px' }}>
                                            <li><button className="dropdown-item" onClick={() => handleFormOpen(evt)}><i className="bi bi-pencil me-2 text-primary"></i> Edit</button></li>
                                            <li><hr className="dropdown-divider" /></li>
                                            <li><button className="dropdown-item text-danger" onClick={() => handleDelete(evt)}><i className="bi bi-trash me-2"></i> Delete</button></li>
                                        </ul>
                                    </div>
                                </div>

                                <div className="mb-auto">
                                    <span className="badge bg-light text-dark border mb-2 px-2 py-1" style={{ fontSize: '11px' }}>
                                        {evt.event_type}
                                    </span>
                                    <h6 className="fw-bold fs-5 text-dark mb-1">{evt.display_name}</h6>
                                    <p className="text-muted small mb-3" style={{ fontSize: '13px' }}>
                                        <code>{evt.event_key}</code>
                                    </p>

                                    {evt.description && (
                                        <p className="text-muted small mb-3 text-truncate" title={evt.description}>
                                            {evt.description}
                                        </p>
                                    )}
                                </div>

                                <div className="mt-3 pt-3 border-top">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <span className="small text-muted fw-semibold">Table Scope</span>
                                        <span className={`badge ${evt.table_scope === 'all' ? 'bg-success bg-opacity-10 text-success' : 'bg-warning bg-opacity-10 text-warning'}`}>
                                            {evt.table_scope.toUpperCase()}
                                        </span>
                                    </div>
                                    {evt.table_scope !== 'all' && (
                                        <div className="text-muted small mt-1 d-flex flex-wrap gap-1">
                                            {evt.scoped_tables?.length || 0} tables selected
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {totalRecords > itemsPerPage && (
                <Paginator
                    page={currentPage}
                    pageCount={pageCount}
                    onPrevious={handlePrevious}
                    onNext={handleNext}
                    onPage={handlePageChange}
                    maxPagesToShow={3}
                />
            )}

            {/* Event Form Modal */}
            {showForm && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }} tabIndex="-1">
                    <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                        <div className="modal-content border-0 shadow" style={{ borderRadius: theme.cardRadius || '12px' }}>
                            <div className="modal-header border-bottom-0 pb-0 pt-4 px-4 bg-white">
                                <h5 className="modal-title fw-bold">{editingEvent ? 'Edit Event Config' : 'New Event Config'}</h5>
                                <button type="button" className="btn-close" onClick={handleFormClose}></button>
                            </div>
                            <div className="modal-body p-4 bg-white">
                                <div className="row g-3">
                                    <div className="col-12 col-md-6">
                                        <label className="fw-bold small text-muted mb-1">Event Category <span className="text-danger">*</span></label>
                                        <select
                                            className={`form-select ${formErrors.event_type ? 'is-invalid' : ''}`}
                                            value={formData.event_type}
                                            onChange={e => {
                                                setFormData({ ...formData, event_type: e.target.value, event_key: '', display_name: '' });
                                            }}
                                            style={{ borderRadius: theme.inputRadius || '6px' }}
                                        >
                                            <option value="">Select Category...</option>
                                            {registry.map(reg => (
                                                <option key={reg.type} value={reg.type}>{reg.display_name}</option>
                                            ))}
                                        </select>
                                        {formErrors.event_type && <div className="text-danger small mt-1"><i className="bi bi-exclamation-circle me-1"></i>{formErrors.event_type}</div>}
                                    </div>

                                    <div className="col-12 col-md-6">
                                        <label className="fw-bold small text-muted mb-1">Specific Event <span className="text-danger">*</span></label>
                                        <select
                                            className={`form-select ${formErrors.event_key ? 'is-invalid' : ''}`}
                                            value={formData.event_key}
                                            onChange={e => handleEventKeyChange(e.target.value)}
                                            disabled={!formData.event_type}
                                            style={{ borderRadius: theme.inputRadius || '6px' }}
                                        >
                                            <option value="">Select Event...</option>
                                            {activeRegistryType?.properties?.event_keys?.filter(ek => {
                                                if (formData.event_type === 'record') return true;
                                                const existing = events.find(e => e.event_key === ek.key);
                                                if (existing) {
                                                    return editingEvent && editingEvent.id === existing.id;
                                                }
                                                return true;
                                            }).map(ek => (
                                                <option key={ek.key} value={ek.key}>{ek.display_name} ({ek.key})</option>
                                            ))}
                                        </select>
                                        {formErrors.event_key && <div className="text-danger small mt-1"><i className="bi bi-exclamation-circle me-1"></i>{formErrors.event_key}</div>}
                                    </div>

                                    <div className="col-12 col-md-12">
                                        <label className="fw-bold small text-muted mb-1">Display Name <span className="text-danger">*</span></label>
                                        <input
                                            type="text"
                                            className={`form-control ${formErrors.display_name ? 'is-invalid' : ''}`}
                                            value={formData.display_name}
                                            onChange={e => setFormData({ ...formData, display_name: e.target.value })}
                                            placeholder="e.g. Order Created"
                                            style={{ borderRadius: theme.inputRadius || '6px' }}
                                        />
                                        {formErrors.display_name && <div className="text-danger small mt-1"><i className="bi bi-exclamation-circle me-1"></i>{formErrors.display_name}</div>}
                                    </div>

                                    <div className="col-12">
                                        <label className="fw-bold small text-muted mb-1">Description (Optional)</label>
                                        <textarea
                                            className="form-control"
                                            rows="2"
                                            value={formData.description}
                                            onChange={e => setFormData({ ...formData, description: e.target.value })}
                                            placeholder="Describe what this event tracks..."
                                            style={{ borderRadius: theme.inputRadius || '6px' }}
                                        ></textarea>
                                    </div>

                                    {formData.event_type === 'record' && (
                                        <div className="col-12 mt-4 pt-3 border-top">
                                            <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                                                <i className="bi bi-funnel text-primary"></i> Table Scope
                                            </h6>
                                            <p className="small text-muted">Restrict this event to only trigger for specific tables.</p>

                                            <div className="d-flex flex-wrap gap-3 mb-3">
                                                {['all', 'include', 'exclude'].map(scope => (
                                                    <label key={scope} className={`btn border py-2 px-3 d-flex align-items-center gap-2 ${formData.table_scope === scope ? 'bg-primary text-white border-primary' : 'bg-light text-dark'}`} style={{ borderRadius: theme.buttonRadius || '6px', cursor: 'pointer', transition: 'all 0.2s', width: '120px', justifyContent: 'center', opacity: formData.table_scope === scope ? 1 : 0.7 }}>
                                                        <input
                                                            type="radio"
                                                            className="d-none"
                                                            name="table_scope"
                                                            value={scope}
                                                            checked={formData.table_scope === scope}
                                                            onChange={() => setFormData({ ...formData, table_scope: scope })}
                                                        />
                                                        <span className="fw-semibold text-capitalize">{scope}</span>
                                                    </label>
                                                ))}
                                            </div>

                                            {formData.table_scope !== 'all' && (
                                                <div className="bg-light p-3 border rounded shadow-sm">
                                                    <label className="fw-bold small text-muted mb-2">Select Tables <span className="text-danger">*</span></label>
                                                    {formErrors.scoped_tables && <div className="text-danger small mb-2"><i className="bi bi-exclamation-circle me-1"></i>{formErrors.scoped_tables}</div>}

                                                    <div className="d-flex flex-wrap gap-2 mh-100 overflow-auto" style={{ maxHeight: '150px' }}>
                                                        {tables.map(table => (
                                                            <label key={table.id} className="d-flex align-items-center gap-2 p-2 border bg-white rounded" style={{ cursor: 'pointer', flexShrink: 0 }}>
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input mt-0"
                                                                    checked={(formData.scoped_tables || []).includes(table.id)}
                                                                    onChange={() => handleToggleScopedTable(table.id)}
                                                                />
                                                                <span className="small text-dark fw-semibold">{table.name}</span>
                                                            </label>
                                                        ))}
                                                        {tables.length === 0 && <span className="small text-muted fst-italic">No tables found for this application.</span>}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="modal-footer border-top-0 pt-0 pb-4 px-4 bg-white d-flex justify-content-end gap-2">
                                <button type="button" className="btn btn-light" onClick={handleFormClose} style={{ borderRadius: theme.buttonRadius || '6px' }}>Cancel</button>
                                <button type="button" className="btn btn-primary shadow-sm" onClick={handleSave} style={{ background: theme.primary, border: 'none', borderRadius: theme.buttonRadius || '6px' }}>
                                    {editingEvent ? 'Save Changes' : 'Create Event'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
